import {
    productionProgressValueStyle,
    productionProgressTrackStyle,
    productionProgressPanelStyle,
    productionProgressLabelStyle,
    productionProgressFillStyle,
    productionAccordionTabStyle,
    productionAddRowButtonStyle,
    productionDossierBlockButtonStyle,
    productionDossierBlockChevronStyle,
    productionDossierBlockContentStyle,
    productionDossierBlockDescriptionStyle,
    productionDossierBlockNumberStyle,
    productionDossierBlockPillStyle,
    productionDossierBlockStyle,
    productionDossierBlockTitleStyle,
    productionEditableFieldsStyle,
    productionEditableRowStyle,
    productionInputStyle,
    productionLabelStyle,
    productionRowDeleteButtonStyle,
    productionRowIndexStyle,
    productionRowsEmptyStyle,
    productionRowsShellStyle,
    productionSectionContentStyle,
    productionSectionDescription,
    productionSectionDescriptionStyle,
    productionSectionHeaderStyle,
    productionSectionMarkerPlaceholderStyle,
    productionSectionNumberStyle,
    productionSectionStyle,
    productionSectionTitleStyle,
} from "./bisProductionStyles";
import { ControleAttachmentPreview } from "../shared/components/ControleAttachmentPreview";
import { buttonBase } from "../shared/styles/layoutStyles";

export function ProductionSection({ title, children }) {
    const titleMatch = String(title || "").match(/^(\d+)\.(.*)$/);
    const titleNumber = titleMatch?.[1] || "";
    const titleText = titleMatch?.[2]?.trimStart() || title;
    const description = productionSectionDescription(titleText);
    return (
        <section style={productionSectionStyle}>
            <div style={productionSectionHeaderStyle}>
                <span style={titleNumber ? productionSectionNumberStyle : productionSectionMarkerPlaceholderStyle}>
                    {titleNumber}
                </span>
                <div style={{ minWidth: 0 }}>
                    <h2 style={productionSectionTitleStyle}>{titleText}</h2>
                    {description && <p style={productionSectionDescriptionStyle}>{description}</p>}
                </div>
            </div>
            <div style={productionSectionContentStyle}>
                {children}
            </div>
        </section>
    );
}

export function ProductionDossierBlock({ block, index, active, onToggle }) {
    return (
        <section style={productionDossierBlockStyle(active)}>
            <button type="button" onClick={onToggle} aria-expanded={active} style={productionDossierBlockButtonStyle(active)}>
                <span style={productionDossierBlockNumberStyle(block.complete)}>
                    {block.complete ? "✓" : index + 1}
                </span>
                <span style={{ minWidth: 0, display: "grid", gap: "3px" }}>
                    <span style={productionDossierBlockTitleStyle}>{block.title}</span>
                    <span style={productionDossierBlockDescriptionStyle}>{productionSectionDescription(String(block.title || "").replace(/^(\d+)\.\s*/, ""))}</span>
                </span>
                <span style={productionDossierBlockPillStyle(block.complete)}>
                    {block.countLabel || (block.complete ? "Renseigné" : "À compléter")}
                </span>
                <span style={productionDossierBlockChevronStyle}>{active ? "⌃" : "›"}</span>
            </button>
            {active && (
                <div style={productionDossierBlockContentStyle}>
                    {block.content}
                </div>
            )}
        </section>
    );
}

export function ProductionField({ label, children, style = {} }) {
    return (
        <label style={{ ...productionLabelStyle, minWidth: 0, ...style }}>
            <span>{label}</span>
            {children}
        </label>
    );
}

export function ProductionEditableRowsSection({ title, rows, onChange, columns, showIndex = true, footer = null }) {
    const safeRows = Array.isArray(rows) ? rows : [];
    const updateRow = (index, key, value) => onChange(safeRows.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
    const addRow = () => onChange([...safeRows, { id: `row-${Date.now()}` }]);
    const removeRow = index => onChange(safeRows.filter((_, rowIndex) => rowIndex !== index));
    const rowGridTemplate = showIndex ? "42px minmax(0, 1fr) 34px" : "minmax(0, 1fr) 34px";

    const content = (
        <>
            <div style={productionRowsShellStyle}>
                {safeRows.length === 0 && (
                    <div style={productionRowsEmptyStyle}>
                        Aucune ligne ajoutée.
                    </div>
                )}
                {safeRows.map((row, index) => (
                    <div key={row.id || index} style={{ ...productionEditableRowStyle, gridTemplateColumns: rowGridTemplate }}>
                        {showIndex && <span style={productionRowIndexStyle}>{index + 1}</span>}
                        <div style={productionEditableFieldsStyle}>
                            {columns.map(([key, label, type]) => (
                                <ProductionField key={key} label={label}>
                                    {type === "textarea" ? (
                                        <textarea style={{ ...productionInputStyle, minHeight: "64px", resize: "vertical" }} value={row[key] || ""} onChange={e => updateRow(index, key, e.target.value)} placeholder={label} />
                                    ) : (
                                        <input style={productionInputStyle} value={row[key] || ""} onChange={e => updateRow(index, key, e.target.value)} placeholder={label} />
                                    )}
                                </ProductionField>
                            ))}
                        </div>
                        <button type="button" aria-label="Supprimer la ligne" onClick={() => removeRow(index)} style={productionRowDeleteButtonStyle}>×</button>
                    </div>
                ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="button" onClick={addRow} style={productionAddRowButtonStyle}>Ajouter une ligne</button>
            </div>
            {footer}
        </>
    );

    return title ? (
        <ProductionSection title={title}>{content}</ProductionSection>
    ) : (
        <div style={{ display: "grid", gap: "12px" }}>{content}</div>
    );
}

export function ProductionDossierAttachments({ dossierId, attachments, uploading, onUpload, onDelete, emptyLabel = "Aucun document importé." }) {
    return (
        <div style={{ display: "grid", gap: "12px" }}>
            <label style={{ ...buttonBase, display: "inline-flex", justifySelf: "start", background: "#2563eb", borderColor: "#2563eb", color: "white", fontWeight: 900, cursor: uploading ? "not-allowed" : "pointer", opacity: uploading ? 0.65 : 1 }}>
                {uploading ? "Import..." : "Importer PDF / image"}
                <input type="file" accept="application/pdf,image/png,image/jpeg" multiple disabled={uploading} onChange={event => { onUpload(event.target.files); event.target.value = ""; }} style={{ display: "none" }} />
            </label>
            {attachments.length === 0 ? (
                <div style={{ padding: "18px", borderRadius: "8px", border: "1px dashed #cbd5e1", background: "#f8fafc", color: "#94a3b8", fontSize: "13px", textAlign: "center" }}>{emptyLabel}</div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "12px" }}>
                    {attachments.map(attachment => <ControleAttachmentPreview key={attachment.id} dossierId={dossierId} attachment={attachment} disabled={uploading} onDelete={onDelete} />)}
                </div>
            )}
        </div>
    );
}

// Blocs dépliables d'un onglet de production, précédés de la progression (part des blocs renseignés).
export function ProductionAccordion({ label, blocks, activeKey, onActiveKeyChange }) {
    const completedCount = blocks.filter(block => block.complete).length;
    const progress = blocks.length ? Math.round((completedCount / blocks.length) * 100) : 0;
    return (
        <div style={productionAccordionTabStyle}>
            <div style={productionProgressPanelStyle}>
                <span style={productionProgressLabelStyle}>{label}</span>
                <span style={productionProgressTrackStyle}>
                    <span style={{ ...productionProgressFillStyle, width: `${progress}%` }} />
                </span>
                <strong style={productionProgressValueStyle}>{progress}%</strong>
            </div>
            {blocks.map((block, index) => (
                <ProductionDossierBlock
                    key={block.key}
                    block={block}
                    index={index}
                    active={activeKey === block.key}
                    onToggle={() => onActiveKeyChange(current => current === block.key ? "" : block.key)}
                />
            ))}
        </div>
    );
}
